/**
 * context/themeContext.tsx
 * SK Upsell is a single, light, monochrome theme by design. The provider keeps
 * the familiar `useTheme()` surface (so every component reads tokens the same
 * way) without a dark mode — there is no toggle to ship.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { LightColors, ColorScheme } from '../theme/colors';
import { FontFamily, FontSize, FontWeight, LineHeight } from '../theme/typography';
import { Spacing } from '../theme/spacing';
import { Radii } from '../theme/radii';
import { Shadows } from '../theme/shadows';
import { Motion } from '../theme/motion';

export interface Theme {
  colors: ColorScheme;
  font: typeof FontFamily;
  fontSize: typeof FontSize;
  fontWeight: typeof FontWeight;
  lineHeight: typeof LineHeight;
  spacing: typeof Spacing;
  radii: typeof Radii;
  shadows: typeof Shadows;
  motion: typeof Motion;
  isDark: boolean;
}

const baseTheme: Theme = {
  colors: LightColors,
  font: FontFamily,
  fontSize: FontSize,
  fontWeight: FontWeight,
  lineHeight: LineHeight,
  spacing: Spacing,
  radii: Radii,
  shadows: Shadows,
  motion: Motion,
  isDark: false,
};

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const value = useMemo<ThemeContextValue>(() => ({ theme: baseTheme, isDark: false }), []);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

/** Non-throwing accessor for components that may render outside the provider. */
export function useThemeSafe(): ThemeContextValue {
  return useContext(ThemeContext) ?? { theme: baseTheme, isDark: false };
}
